import type { CollectionItem } from '@panelvault/shared';
import { Trash2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Checkbox, Textarea } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useDeleteCollectionItem, useUpdateCollectionItem } from './queries';

interface CollectionItemDialogProps {
  open: boolean;
  onClose: () => void;
  item: CollectionItem | null;
  title: string;
  knownLocations: string[];
}

export function CollectionItemDialog({
  open,
  onClose,
  item,
  title,
  knownLocations,
}: CollectionItemDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const updateItem = useUpdateCollectionItem();
  const deleteItem = useDeleteCollectionItem();

  const [quantity, setQuantity] = useState('1');
  const [condition, setCondition] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [location, setLocation] = useState('');
  const [isRead, setIsRead] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !item) return;
    setQuantity(String(item.quantity));
    setCondition(item.condition ?? '');
    setPurchasePrice(item.purchasePrice?.toString() ?? '');
    setPurchaseDate(item.purchaseDate ?? '');
    setLocation(item.location ?? '');
    setIsRead(item.isRead);
    setNotes(item.notes ?? '');
    setError(null);
  }, [open, item]);

  const pending = updateItem.isPending || deleteItem.isPending;

  async function submit() {
    if (!item) return;

    try {
      await updateItem.mutateAsync({
        id: item.id,
        patch: {
          quantity: Math.max(1, Number(quantity) || 1),
          condition: condition.trim() || null,
          purchasePrice:
            purchasePrice.trim() === '' ? null : Number(purchasePrice.replace(',', '.')),
          purchaseDate: purchaseDate.trim() || null,
          location: location.trim() || null,
          isRead,
          notes: notes.trim() || null,
        },
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  async function remove() {
    if (!item) return;
    try {
      await deleteItem.mutateAsync(item.id);
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('collection.editCopy')}
      description={title}
      footer={
        <>
          <Button variant="danger" onClick={() => void remove()} disabled={pending}>
            <Trash2 className="size-4" />
            {t('collection.removeCopy')}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('form.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={pending}>
            {pending ? t('form.saving') : t('form.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('collection.quantity')} htmlFor={`${ids}-quantity`}>
            <Input
              id={`${ids}-quantity`}
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>

          <Field
            label={t('collection.condition')}
            htmlFor={`${ids}-condition`}
            hint={t('collection.conditionHint')}
          >
            <Input
              id={`${ids}-condition`}
              value={condition}
              onChange={(event) => setCondition(event.target.value)}
            />
          </Field>

          <Field label={t('collection.purchasePrice')} htmlFor={`${ids}-price`}>
            <Input
              id={`${ids}-price`}
              inputMode="decimal"
              value={purchasePrice}
              onChange={(event) => setPurchasePrice(event.target.value)}
            />
          </Field>

          <Field label={t('collection.purchaseDate')} htmlFor={`${ids}-date`}>
            <Input
              id={`${ids}-date`}
              type="date"
              value={purchaseDate}
              onChange={(event) => setPurchaseDate(event.target.value)}
            />
          </Field>
        </div>

        <Field label={t('collection.location')} htmlFor={`${ids}-location`}>
          <Input
            id={`${ids}-location`}
            list={`${ids}-locations`}
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
          <datalist id={`${ids}-locations`}>
            {knownLocations.map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
        </Field>

        <Checkbox
          label={t('collection.isRead')}
          checked={isRead}
          onChange={(event) => setIsRead(event.target.checked)}
        />

        <Field label={t('form.notes')} htmlFor={`${ids}-notes`}>
          <Textarea
            id={`${ids}-notes`}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
